import { getPropClassesFromInstance } from 'lib/getPropClasses';
import cx from 'classnames';
// @ts-ignore
import LayerPropTypes from './lib/LayerPropTypes';
// @ts-ignore
import pickGlobalElementProps from 'utils/pickInputElementProps';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
// @ts-ignore
import withLayerHideOnBlurTrigger from 'components/lib/withLayerHideOnBlurTrigger';
// @ts-ignore
import withLayerHideOnEscapeTrigger from 'components/lib/withLayerHideOnEscapeTrigger';
// @ts-ignore
import withLayerHideOnTransitionTrigger from 'components/lib/withLayerHideOnTransitionTrigger';
// @ts-ignore
import findZIndex from 'utils/findZIndex';

import './styles/Layer.scss';

/**
 * ## System for displaying content in a "layer" above the page, such as [Dialog](/internal/components/Dialog) & [Tooltip](/internal/components/Tooltip), without manual positioning or `z-index`.
 *
 * The system manages the positioning of the element on all axis. It optimizes the display of a
 * layer by positioning it within the visible viewport. The `z-index` is managed by appending
 * new Layers to the end of the DOM & rely on DOM ordering, so only the `z-index` of the
 * [LayerTriggers](/internal/components/LayerTrigger) are needed to ensure all layers * nested layers
 * (e.g., a Tooltip from a Menu item inside of a Dialog) stack properly.
 *
 *
 * # Implemenation
 *
 * Compose a `Layer`, then trigger with a [LayerTrigger](/internal/components/LayerTrigger).
 *
 *
 * ```example
 * <LayerTrigger layer={<Tooltip>Tooltip</Tooltip>}>
 *   <Button>Hover</Button>
 * </LayerTrigger>
 * ```
 */

class Layer extends React.Component<any> {
  static propTypes = {
    ...LayerPropTypes,
    /** LayerTrigger */
    context: PropTypes.object,
    /** LayerTrigger */
    fixed: PropTypes.bool,
    /** LayerTrigger */
    position: PropTypes.shape({
      top: PropTypes.number,
      left: PropTypes.number,
    }),
    /** Clicks outside layer do not propagate */
    propagateOutsideClicks: PropTypes.bool,
    /** LayerTrigger */
    zIndex: PropTypes.number,
    /** LayerTrigger */
    onRequestHide: PropTypes.func,
  };

  static propClasses = {
    alignment: {
      middle: null,
      top: 'Layer-alignmentTop',
      right: 'Layer-alignmentRight',
      bottom: 'Layer-alignmentBottom',
      left: 'Layer-alignmentLeft',
    },
    placement: {
      top: 'Layer-placementTop',
      right: 'Layer-placementRight',
      bottom: 'Layer-placementBottom',
      left: 'Layer-placementLeft',
    },
    fixed: 'Layer-fixed',
  };

  /* eslint-disable react/default-props-match-prop-types */
  // linter not detecting the props from ...LayerPropTypes above
  static defaultProps = {
    alignment: 'middle',
    placement: 'right',
    propagateOutsideClicks: true,
  };

  layerContainerParentNode: (Node & ParentNode) | undefined | null;
  clickLayer: HTMLDivElement | undefined;

  componentDidMount() {
    if (!this.props.propagateOutsideClicks) {
      const layerNode = ReactDOM.findDOMNode(this);
      const layerContainerNode = layerNode && layerNode.parentNode;
      this.layerContainerParentNode = layerContainerNode && layerContainerNode.parentNode;
      this.clickLayer = document.createElement('div');
      const zIndex = findZIndex(layerNode) - 1;
      this.clickLayer.style.cssText = `z-index:${zIndex};position:fixed;top:0;width:100%;height:100%;`;
      if (this.layerContainerParentNode) {
        this.layerContainerParentNode.insertBefore(this.clickLayer, layerContainerNode);
      }
    }
  }

  componentWillUnmount() {
    if (!this.props.propagateOutsideClicks && this.layerContainerParentNode && this.clickLayer) {
      this.layerContainerParentNode.removeChild(this.clickLayer);
    }
  }

  render() {
    const { children, className, position, style, zIndex, ...others } = this.props;
    const divProps = pickGlobalElementProps(others);
    const divStyle = Object.assign({}, style, position);

    if (zIndex) {
      divStyle.zIndex = zIndex;
    }

    const anyCast: any = this;

    return (
      <div
        {...divProps}
        className={cx('Layer', getPropClassesFromInstance(anyCast), className)}
        style={divStyle}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}>
        {children}
      </div>
    );
  }
}

export { Layer };
export default withLayerHideOnTransitionTrigger(
  withLayerHideOnEscapeTrigger(withLayerHideOnBlurTrigger(Layer))
);
